import { Star, ArrowUpRight } from "lucide-react";
import { useLang, MAPS_URL } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const AIRBNB_URL = "https://www.airbnb.it/rooms/26104436";

const Stars = ({ className = "h-4 w-4" }) => (
  <span className="flex gap-1 text-terra">
    {Array.from({ length: 5 }).map((_, i) => (
      <Star key={i} className={className} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

const AIRBNB_QUOTES = [
  {
    text: "Questa è davvero una casa fantastica, dotata di tutto. La piscina è fantastica. L'host ci ha accolto in modo incredibilmente caloroso… Ci piacerebbe molto tornare!",
    author: "Marianne",
  },
  {
    text: "Il posto è bellissimo: tra palme e montagne, piscina e tranquillità. Lo consiglio al 200%!",
    author: "Matthieu",
  },
  {
    text: "Soggiorno fantastico, persino meglio delle foto. I giardini sono molto curati, la vista sulle montagne è incredibile… e la piscina è enorme.",
    author: "Natalia",
  },
];

export default function Reviews() {
  const { t } = useLang();

  return (
    <section id="recensioni" className="border-y border-line bg-sand2 py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-5 md:px-10">
        <div className="flex flex-col items-center text-center">
          <Reveal>
            <Eyebrow>{t.reviews.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-serif text-4xl font-light md:text-6xl">{t.reviews.title}</h2>
          </Reveal>
        </div>

        <div className="mt-14 grid gap-6 md:mt-20 md:grid-cols-3">
          <Reveal delay={0.1}>
            <div
              data-testid="reviews-airbnb-card"
              className="relative flex h-full flex-col items-center rounded-lg bg-ink px-8 py-10 text-center text-sand"
            >
              <span className="absolute right-4 top-4 rounded-full bg-[#FF385C] px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                Airbnb
              </span>
              <p className="font-serif text-6xl font-light leading-none md:text-7xl">4,95</p>
              <div className="mt-4">
                <Stars className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm uppercase tracking-[0.22em] text-sand/80">
                87 recensioni su Airbnb
              </p>
              <span className="mt-4 flex items-center gap-2 rounded-full border border-terra/60 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-terra">
                ★ Superhost · Ilenia · 8 anni
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div
              data-testid="reviews-booking-card"
              className="relative flex h-full flex-col items-center rounded-lg bg-[#003580] px-8 py-10 text-center text-white"
            >
              <span className="absolute right-4 top-4 rounded-full bg-white px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#003580]">
                Booking.com
              </span>
              <p className="font-serif text-6xl font-light leading-none md:text-7xl">10,0</p>
              <div className="mt-4">
                <Stars className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm uppercase tracking-[0.22em] text-white/80">
                Eccezionale · 2 recensioni
              </p>
              <span className="mt-4 rounded-full border border-white/50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/90">
                Pulizia · Comfort · Staff 10/10
              </span>
            </div>
          </Reveal>

          <Reveal delay={0.22}>
            <div
              data-testid="reviews-google-card"
              className="relative flex h-full flex-col items-center rounded-lg border border-line bg-sand px-8 py-10 text-center"
            >
              <span className="absolute right-4 top-4 rounded-full bg-[#4285F4] px-3.5 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white">
                Google
              </span>
              <p className="font-serif text-6xl font-light leading-none text-ink md:text-7xl">5,0</p>
              <div className="mt-4">
                <Stars className="h-5 w-5" />
              </div>
              <p className="mt-3 text-sm uppercase tracking-[0.22em] text-ink2">
                9 recensioni su Google
              </p>
              <span className="mt-4 rounded-full border border-line px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-ink2">
                Verificate
              </span>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 md:mt-24">
          <Reveal>
            <Eyebrow light>Recensioni Airbnb</Eyebrow>
          </Reveal>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {AIRBNB_QUOTES.map((q, i) => (
              <Reveal key={q.author} delay={0.08 * i}>
                <figure
                  data-testid="airbnb-quote-card"
                  className={`flex h-full flex-col justify-between rounded-lg border p-8 transition-transform duration-500 hover:-translate-y-1.5 ${
                    i === 0
                      ? "border-ink bg-ink text-sand md:row-span-1"
                      : "border-line bg-sand text-ink"
                  }`}
                >
                  <div>
                    <Stars />
                    <blockquote className="mt-5 font-serif text-xl font-light italic leading-snug md:text-2xl">
                      “{q.text}”
                    </blockquote>
                  </div>
                  <figcaption className="mt-6 flex items-center justify-between">
                    <span className="text-[11px] uppercase tracking-[0.22em] text-ink2">
                      {q.author} · Airbnb
                    </span>
                    <span className="font-serif text-lg italic text-terra">4,95</span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-16 grid gap-5 md:mt-24 md:grid-cols-3">
          {t.reviews.quotes.map((quote, i) => (
            <Reveal key={i} delay={0.1 * i}>
              <figure
                data-testid="review-card"
                className="flex h-full flex-col justify-between rounded-lg border border-line bg-sand p-8 transition-transform duration-500 hover:-translate-y-1.5"
              >
                <div>
                  <Stars />
                  <blockquote className="mt-5 font-serif text-2xl font-light italic leading-snug">
                    “{quote}”
                  </blockquote>
                </div>
                <figcaption className="mt-6 text-[11px] uppercase tracking-[0.25em] text-ink2">
                  {t.reviews.source}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2} className="mt-14 flex justify-center">
          <a
            href={MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-read-all"
            className="group flex items-center gap-2 rounded-full border border-ink/20 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink transition-all duration-300 hover:bg-ink hover:text-sand"
          >
            {t.reviews.readAll}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </Reveal>
      </div>
    </section>
  );
}
